import React, { useState, useEffect } from 'react';
import haukeApiService from '../../services/haukeApi';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import '../../assets/css/profil-admin.css';

const Notizen = ({ hofId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNote, setNewNote] = useState({
    text: '',
    type: 'persönlich',
    isTask: false,
    dueDate: '',
    priority: 'normal'
  });

  const contactTypes = [
    { id: 'telefon', label: 'Telefon', icon: 'phone', color: '#22c55e' },
    { id: 'handy', label: 'Handy', icon: 'smartphone', color: '#3b82f6' },
    { id: 'mail', label: 'E-Mail', icon: 'mail', color: '#f59e0b' },
    { id: 'persönlich', label: 'Persönlich', icon: 'person', color: '#8b5cf6' }
  ];

  const priorities = [
    { id: 'low', label: 'Niedrig', color: '#64748b' },
    { id: 'normal', label: 'Normal', color: '#0ea5e9' },
    { id: 'high', label: 'Hoch', color: '#f97316' },
    { id: 'urgent', label: 'Dringend', color: '#dc2626' }
  ];

  useEffect(() => {
    loadData();
  }, [hofId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Parallel loading: Tasks aus /tasks + Notizen aus /direkterzeuger
      const [direkterzeugerResponse, tasksResponse] = await Promise.all([
        haukeApiService.getDirekterzeugerDetails(hofId),
        haukeApiService.getAllTasks({ hofId, limit: 1000 }) // Alle Tasks für diesen Direkterzeuger
      ]);
      
      const direkterzeugerData = direkterzeugerResponse.data;
      const tasksData = tasksResponse.data || [];
      
      // Nur echte Notizen aus direkterzeuger/notes (isTask: false)
      const realNotes = (direkterzeugerData.notes || []).filter(note => note.isTask !== true);
      
      // Kombinierte Daten für UI
      const combinedData = {
        ...direkterzeugerData,
        notes: realNotes,
        tasks: tasksData
      };
      
      setData(combinedData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };



  const handleAddNote = async () => {
    if (!newNote.text.trim()) return;

    try {
      setSaving(true);
      const itemToAdd = {
        id: Date.now().toString(),
        text: newNote.text,
        type: newNote.type,
        isTask: newNote.isTask,
        dueDate: newNote.isTask ? newNote.dueDate : null,
        priority: newNote.isTask ? newNote.priority : 'normal',
        completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (newNote.isTask) {
        // TASK: Erstelle über API (Single Source of Truth)
        const taskData = {
          id: itemToAdd.id,
          text: itemToAdd.text,
          type: itemToAdd.type,
          isTask: true,
          dueDate: itemToAdd.dueDate,
          priority: itemToAdd.priority,
          completed: false,
          createdAt: itemToAdd.createdAt,
          updatedAt: itemToAdd.updatedAt,
          hofId: hofId,
          direkterzeugerName: data.name || 'Unbekannt',
          direkterzeugerCity: data.city || 'Unbekannt'
        };
        
        await haukeApiService.createTask(taskData);
        showSuccessToast('Aufgabe erfolgreich hinzugefügt!');
        
      } else {
        // NOTIZ: Speichere nur in /direkterzeuger/notes
        const currentNotes = data.notes || [];
        const updatedNotes = [...currentNotes, itemToAdd];
        
        await haukeApiService.updateDirekterzeuger(hofId, { notes: updatedNotes });
        showSuccessToast('Notiz erfolgreich hinzugefügt!');
      }
      
      // Vollständiges Neuladen der Daten nach erfolgreicher Operation
      await loadData();
      
      setNewNote({
        text: '',
        type: 'persönlich',
        isTask: false,
        dueDate: '',
        priority: 'normal'
      });
      setShowAddForm(false);
      
    } catch (err) {
      console.error('Error adding note/task:', err);
      setError('Fehler beim Hinzufügen: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (itemId) => {
    // Finde das Item (Task oder Notiz) in der kombinierten Liste
    const item = [...(data.notes || []), ...(data.tasks || [])].find(i => i.id === itemId);
    
    if (!item) {
      setError('Element nicht gefunden');
      return;
    }
    
    const isTask = (data.tasks || []).some(t => t.id === itemId);
    const confirmMessage = isTask ? 'Aufgabe wirklich löschen?' : 'Notiz wirklich löschen?';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      setSaving(true);
      
      if (isTask) {
        // TASK: Lösche über API (Single Source of Truth)
        await haukeApiService.deleteTask(hofId, itemId);
        showSuccessToast('Aufgabe erfolgreich gelöscht!');
        
      } else {
        // NOTIZ: Lösche aus /direkterzeuger/notes
        const currentNotes = data.notes || [];
        const updatedNotes = currentNotes.filter(note => note.id !== itemId);
        
        await haukeApiService.updateDirekterzeuger(hofId, { notes: updatedNotes });
        showSuccessToast('Notiz erfolgreich gelöscht!');
      }
      
      // Vollständiges Neuladen der Daten nach erfolgreicher Operation
      await loadData();
      
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Fehler beim Löschen: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTask = async (taskId) => {
    try {
      setSaving(true);
      
      // Finde Task in der lokalen tasks array
      const currentTasks = data.tasks || [];
      const taskToUpdate = currentTasks.find(task => task.id === taskId);
      
      if (!taskToUpdate) {
        throw new Error('Task nicht gefunden');
      }
      
      const newCompletedStatus = !taskToUpdate.completed;
      
      // Aktualisiere Task über API (Single Source of Truth)
      await haukeApiService.updateTask(hofId, taskId, {
        completed: newCompletedStatus,
        updatedAt: new Date().toISOString()
      });
      
      showSuccessToast(newCompletedStatus ? 'Aufgabe als erledigt markiert!' : 'Aufgabe als offen markiert!');
      
      // Vollständiges Neuladen der Daten nach erfolgreicher Operation
      await loadData();
      
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Fehler beim Aktualisieren der Aufgabe: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getTypeInfo = (type) => contactTypes.find(t => t.id === type) || contactTypes[3];
  const getPriorityInfo = (priority) => priorities.find(p => p.id === priority) || priorities[1];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) return <div className="webling-loading">Daten werden geladen...</div>;
  if (error) return <div className="webling-error">{error}</div>;
  if (!data) return <div className="webling-error">Keine Daten verfügbar</div>;

  const notes = data.notes || [];
  const tasks = data.tasks || [];
  
  // Kombinierte Liste für Anzeige
  const combinedItems = [
    ...notes.map(note => ({ ...note, itemType: 'note' })),
    ...tasks.map(task => ({ ...task, itemType: 'task' }))
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="webling-container">
      <div className="webling-content">
        <div className="notes-header">
          <div className="notes-stats">
            <div className="stat-badge">
              <span className="stat-number">{notes.length}</span>
              <span className="stat-label">Notizen</span>
            </div>
            <div className="stat-badge">
              <span className="stat-number">{tasks.filter(t => !t.completed).length}</span>
              <span className="stat-label">Offene Aufgaben</span>
            </div>
            <div className="stat-badge">
              <span className="stat-number">{tasks.filter(t => t.dueDate && isOverdue(t.dueDate) && !t.completed).length}</span>
              <span className="stat-label">Überfällig</span>
            </div>
          </div>
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => setShowAddForm(!showAddForm)}
            disabled={saving}
          >
            <i className="material-icons">add</i>
            Neue Notiz
          </button>
        </div>

        {showAddForm && (
          <div className="add-note-form">
            <div className="form-row">
              <textarea
                value={newNote.text}
                onChange={(e) => setNewNote({ ...newNote, text: e.target.value })}
                placeholder="Notiz eingeben..."
                rows={3}
                disabled={saving}
              />
            </div>

            <div className="form-row-inline">
              <div className="form-group-sm">
                <label>Kontakt</label>
                <select
                  value={newNote.type}
                  onChange={(e) => setNewNote({ ...newNote, type: e.target.value })}
                  disabled={saving}
                >
                  {contactTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-sm">
                <label className="checkbox-label-sm">
                  <input
                    type="checkbox"
                    checked={newNote.isTask}
                    onChange={(e) => setNewNote({ ...newNote, isTask: e.target.checked })}
                    disabled={saving}
                  />
                  <span>Als Aufgabe</span>
                </label>
              </div>

              {newNote.isTask && (
                <>
                  <div className="form-group-sm">
                    <label>Fälligkeit</label>
                    <input
                      type="datetime-local"
                      value={newNote.dueDate}
                      onChange={(e) => setNewNote({ ...newNote, dueDate: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="form-group-sm">
                    <label>Priorität</label>
                    <select
                      value={newNote.priority}
                      onChange={(e) => setNewNote({ ...newNote, priority: e.target.value })}
                      disabled={saving}
                    >
                      {priorities.map(priority => (
                        <option key={priority.id} value={priority.id}>{priority.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="form-actions">
              <button 
                className="btn btn-primary btn-sm"
                onClick={handleAddNote}
                disabled={saving || !newNote.text.trim()}
              >
                {saving ? 'Speichern...' : 'Hinzufügen'}
              </button>
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => setShowAddForm(false)}
                disabled={saving}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <div className="notes-list">
          {combinedItems.length > 0 ? (
            combinedItems.map(item => {
              const typeInfo = getTypeInfo(item.type);
              const priorityInfo = getPriorityInfo(item.priority);
              const isTask = item.itemType === 'task';
              const isTaskOverdue = isTask && item.dueDate && isOverdue(item.dueDate) && !item.completed;

              return (
                <div 
                  key={item.id} 
                  className={`note-item ${isTask ? 'note-task' : 'note-regular'} ${item.completed ? 'note-completed' : ''} ${isTaskOverdue ? 'note-overdue' : ''}`}
                >
                  <div className="note-header">
                    <div className="note-meta">
                      <span 
                        className="note-type-badge"
                        style={{ backgroundColor: typeInfo.color + '20', color: typeInfo.color }}
                      >
                        <i className="material-icons">{typeInfo.icon}</i>
                        {typeInfo.label}
                      </span>
                      {isTask && (
                        <span 
                          className="note-priority-badge"
                          style={{ backgroundColor: priorityInfo.color + '20', color: priorityInfo.color }}
                        >
                          {priorityInfo.label}
                        </span>
                      )}
                      <span className="note-date">
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                    <div className="note-actions">
                      {isTask && (
                        <button
                          className={`btn-task-toggle ${item.completed ? 'completed' : ''}`}
                          onClick={() => handleToggleTask(item.id)}
                          disabled={saving}
                          title={item.completed ? 'Als offen markieren' : 'Als erledigt markieren'}
                        >
                          <i className="material-icons">
                            {item.completed ? 'check_circle' : 'radio_button_unchecked'}
                          </i>
                        </button>
                      )}
                      <button
                        className="btn-delete-note"
                        onClick={() => handleDeleteNote(item.id)}
                        disabled={saving}
                        title={isTask ? 'Aufgabe löschen' : 'Notiz löschen'}
                      >
                        <i className="material-icons">delete</i>
                      </button>
                    </div>
                  </div>

                  <div className="note-content">
                    <p>{item.text}</p>
                    {isTask && item.dueDate && (
                      <div className={`note-due-date ${isTaskOverdue ? 'overdue' : ''}`}>
                        <i className="material-icons">schedule</i>
                        Fällig: {formatDate(item.dueDate)}
                        {isTaskOverdue && <span className="overdue-label">ÜBERFÄLLIG</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <i className="material-icons">sticky_note_2</i>
              <h3>Keine Notizen vorhanden</h3>
              <p>Erstellen Sie Ihre erste Notiz oder Aufgabe.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notizen; 